import React from 'react';
import { Pedido, ItemPedido, OrderStatus } from '../../types';
import { formatCurrency, getStatusBadge } from '../../constants';

interface AdminPedidosProps {
  pedidos: Pedido[];
  selectedPedido: Pedido | null;
  setSelectedPedido: (p: Pedido | null) => void;
  itens: ItemPedido[];
  openPedido: (p: Pedido) => void;
  updateStatus: (pId: string, status: OrderStatus) => void;
  activeTab: 'entrada' | 'producao' | 'historico';
  setActiveTab: (tab: 'entrada' | 'producao' | 'historico') => void;
  isUpdating: boolean;
  isDemo?: boolean;
}

export const AdminPedidos: React.FC<AdminPedidosProps> = ({
  pedidos,
  selectedPedido,
  setSelectedPedido,
  itens,
  openPedido,
  updateStatus,
  activeTab,
  setActiveTab,
  isUpdating,
  isDemo = false
}) => {
  const filteredPedidos = pedidos.filter(p => {
    if (activeTab === 'entrada') return p.status === OrderStatus.NOVO;
    if (activeTab === 'producao') return [OrderStatus.ACEITO, OrderStatus.PREPARO, OrderStatus.ENTREGA].includes(p.status);
    return p.status === OrderStatus.CONCLUIDO;
  });

  return (
    <div className="flex flex-col md:flex-row h-full animate-fade-in relative">
      {/* Lista Lateral - Oculta no mobile se um pedido estiver aberto */}
      <div className={`w-full md:w-80 border-r border-slate-200 flex flex-col bg-slate-50/50 ${selectedPedido ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-4 grid grid-cols-3 gap-2 bg-white border-b border-slate-100 sticky top-0 z-10">
          {(['entrada', 'producao', 'historico'] as const).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} className={`py-2.5 text-[10px] font-black uppercase rounded-xl transition-all relative ${activeTab === tab ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:bg-slate-100'}`}>
              {tab === 'entrada' ? 'Novos' : tab === 'producao' ? 'Ativos' : 'Fim'}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
          {filteredPedidos.map(p => (
            <div key={p.id} onClick={() => openPedido(p)} className={`p-5 rounded-2xl border-2 transition-all cursor-pointer ${selectedPedido?.id === p.id ? 'border-orange-600 bg-white shadow-xl' : 'border-transparent bg-white hover:border-slate-200 shadow-sm'}`}>
              <div className="flex justify-between items-start mb-4">
                <h4 className="font-black text-slate-800 text-sm truncate pr-2">{p.nome_cliente}</h4>
                <span className="text-[9px] font-bold text-slate-400 shrink-0">{new Date(p.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
              </div>
              <div className="flex justify-between items-center">
                {getStatusBadge(p.status)}
                <span className="font-black text-slate-900 text-xs">{formatCurrency(p.total)}</span>
              </div>
            </div>
          ))}
          {filteredPedidos.length === 0 && (
            <div className="py-20 text-center opacity-40">
              <p className="text-xs font-black uppercase tracking-widest text-slate-400 px-4">Sem pedidos nesta categoria</p>
            </div>
          )}
        </div>
      </div>

      {/* Área de Detalhes - Ocupa tela cheia no mobile se um pedido estiver aberto */}
      <div className={`flex-1 bg-white overflow-y-auto p-4 md:p-12 ${selectedPedido ? 'block' : 'hidden md:block'}`}>
        {selectedPedido ? (
          <div className="max-w-3xl mx-auto space-y-6 md:space-y-10 animate-fade-in pb-10">
            {/* Botão Voltar Mobile */}
            <button onClick={() => setSelectedPedido(null)} className="md:hidden flex items-center gap-2 text-slate-400 font-bold text-xs uppercase tracking-widest mb-4 hover:text-slate-900 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 19l-7-7 7-7"></path></svg>
              Voltar para lista
            </button>

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-6 md:pb-10 border-b-2 border-slate-50 gap-6">
              <div>
                <h3 className="text-2xl md:text-4xl font-black tracking-tighter mb-1 text-slate-900">Comanda #{selectedPedido.id.slice(0,6).toUpperCase()}</h3>
                <p className="text-slate-400 font-bold text-[10px] md:text-xs uppercase tracking-widest">{selectedPedido.nome_cliente} • {new Date(selectedPedido.created_at).toLocaleTimeString()}</p>
              </div>
              <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                {selectedPedido.status === OrderStatus.NOVO && (<button onClick={() => updateStatus(selectedPedido.id, OrderStatus.ACEITO)} className={`flex-1 sm:flex-none bg-emerald-600 text-white px-6 py-4 rounded-2xl font-black shadow-xl hover:bg-emerald-700 transition-all text-xs uppercase tracking-widest ${isDemo ? 'opacity-40 cursor-not-allowed' : ''}`}>Aceitar</button>)}
                {(selectedPedido.status === OrderStatus.ACEITO || selectedPedido.status === OrderStatus.PREPARO) && (<button onClick={() => updateStatus(selectedPedido.id, OrderStatus.ENTREGA)} className={`flex-1 sm:flex-none bg-blue-600 text-white px-6 py-4 rounded-2xl font-black shadow-xl hover:bg-blue-700 transition-all text-xs uppercase tracking-widest ${isDemo ? 'opacity-40 cursor-not-allowed' : ''}`}>Entregar</button>)}
                {[OrderStatus.ENTREGA, OrderStatus.ACEITO, OrderStatus.PREPARO].includes(selectedPedido.status) && (<button onClick={() => updateStatus(selectedPedido.id, OrderStatus.CONCLUIDO)} className={`flex-1 sm:flex-none bg-slate-900 text-white px-6 py-4 rounded-2xl font-black shadow-xl hover:bg-black transition-all text-xs uppercase tracking-widest ${isDemo ? 'opacity-40 cursor-not-allowed' : ''}`}>Concluir</button>)}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              <div className="space-y-6">
                <h5 className="text-[10px] md:text-[11px] font-black text-slate-400 uppercase tracking-widest">Itens da Comanda</h5>
                <div className="bg-slate-50 border border-slate-100 rounded-3xl overflow-hidden divide-y divide-slate-200">
                  {itens.map(it => (
                    <div key={it.id} className="p-4 md:p-6 flex flex-col gap-2">
                      <div className="flex justify-between gap-4">
                        <div className="flex gap-3">
                          <span className="bg-white border border-slate-200 text-slate-900 px-2 py-0.5 rounded-lg font-black text-xs h-fit">{it.quantidade}x</span>
                          <div>
                            <p className="font-black text-slate-900 text-sm leading-tight">{it.produto_nome}</p>
                            {it.observacao && <p className="text-[11px] text-orange-600 font-bold italic mt-1 leading-snug">"{it.observacao}"</p>}
                          </div>
                        </div>
                        <span className="font-black text-slate-400 text-xs shrink-0">{formatCurrency(it.preco_unitario * it.quantidade)}</span>
                      </div>
                    </div>
                  ))}
                  <div className="p-6 md:p-8 bg-slate-900 text-white flex justify-between items-center">
                    <span className="font-black uppercase text-[10px] md:text-[11px] tracking-widest">Total Líquido</span>
                    <span className="text-2xl md:text-3xl font-black text-orange-500">{formatCurrency(selectedPedido.total)}</span>
                  </div>
                </div>
              </div>
              <div className="space-y-8">
                <div className="space-y-4">
                  <h5 className="text-[10px] md:text-[11px] font-black text-slate-400 uppercase tracking-widest">Dados de Entrega</h5>
                  <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm space-y-5">
                    <div>
                        <p className="text-[9px] font-black text-slate-300 uppercase tracking-widest mb-1">WhatsApp Cliente</p>
                        <a href={`https://wa.me/${selectedPedido.whatsapp_cliente.replace(/\D/g,'')}`} target="_blank" className="font-black text-slate-900 text-sm flex items-center gap-2 hover:text-orange-600 transition-colors">
                            {selectedPedido.whatsapp_cliente}
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg>
                        </a>
                    </div>
                    <div>
                        <p className="text-[9px] font-black text-slate-300 uppercase tracking-widest mb-1">Localização</p>
                        <p className="text-sm font-bold text-slate-700 leading-relaxed bg-slate-50 p-4 rounded-2xl border border-slate-100">{selectedPedido.endereco}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center opacity-40 select-none px-6 text-center">
            <div className="w-24 md:w-32 h-24 md:h-32 bg-slate-50 rounded-3xl md:rounded-[3rem] flex items-center justify-center mb-6">
              <svg className="w-12 md:w-16 h-12 md:h-16 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path></svg>
            </div>
            <h4 className="text-xl md:text-2xl font-black text-slate-300 tracking-tighter uppercase">Escolha um pedido para gerenciar</h4>
          </div>
        )}
      </div>
    </div>
  );
};